import React, { useEffect, useState } from "react";
import { getAllAgents, searchAgent } from "../../services/AgentService.js";
import { Filter } from "lucide-react";
import { StatusBadge } from "../SharedInputs.jsx";

function AgentList({ onSelectAgent, selectedAgent }) {
  const [agents, setAgents] = useState([]);
  const [activeStatus, setActiveStatus] = useState("active");
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("existing");

  const fetchAgents = async () => {
    try {
      const data = await searchAgent(searchText);
      setAgents(data || []);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const newAgents = agents.filter((s) => s?.status === "new");
  const existingAgents = agents.filter((s) => s?.status === "active" || s?.status === "inactive" || s?.status === "suspended");

  let displayedAgents = activeTab === "new" ? newAgents : existingAgents;

  useEffect(() => {
    fetchAgents();
  }, [searchText]);
  // Add this function inside the component
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setActiveStatus("all"); // ← reset dropdown when switching tabs
  };

  useEffect(() => {
    if (!selectedAgent) return;
    setAgents((prev) => {
      const exists = prev.find((a) => a.id === selectedAgent.id);
      if (exists) {
        return prev.map((a) =>
          a.id === selectedAgent.id ? selectedAgent : a
        );
      } else {
        return [...prev, selectedAgent];
      }
    });
  }, [selectedAgent]);

  // Count per status
  const statusCounts = agents.reduce(
    (acc, agent) => {
      acc[agent.status] = (acc[agent.status] || 0) + 1;
      return acc;
    },
    { active: 0, inactive: 0, suspended: 0 }
  );

  const filteredAgents =
    activeStatus === "all"
      ? displayedAgents
      : displayedAgents.filter(
          (a) => a.status.toLowerCase() === activeStatus.toLowerCase()
        );

  // --- UI styles ---
  const barWrap = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: 16,
    gap: "1rem",
  };

  const chip = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "0.7rem",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };

  const labelStyle = { fontSize: 13, color: "#6b7280", fontWeight: 600 };
  const selectStyle = {
    border: "none",
    background: "transparent",
    fontSize: 14,
    outline: "none",
    padding: "4px 6px",
    cursor: "pointer",
  };

  const searchInputStyle = {
    maxWidth: "300px",
    flex: 1,
  };

  return (
    <div className="record-list">
      {/* Search and Filter on the same line */}
      <div style={barWrap}>
        <input
          type="text"
          placeholder="Search by Code, Name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="form-input"
          style={searchInputStyle}
        />

        {/* Show status filter ONLY for existing agents */}
        {activeTab === "existing" && (
          <div style={chip}>
            <Filter size={16} color="#6b7280" />
            <span style={labelStyle}>Status</span>
            <select
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              style={selectStyle}
              aria-label="Filter agents by status"
            >
              <option value="all">All ({existingAgents.length})</option>
              <option value="active">
                Active ({statusCounts.active || 0})
              </option>
              <option value="inactive">
                Inactive ({statusCounts.inactive || 0})
              </option>
              <option value="suspended">
                Suspended ({statusCounts.suspended || 0})
              </option>
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        {[
          {
            key: "existing",
            label: `Existing Agents (${existingAgents.length})`,
          },
          {
            key: "new",
            label: `New Agent Applications (${newAgents.length})`,
          },
        ].map((tab) => (
          <div
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            style={{
              padding: "0.5rem 1.2rem",
              cursor: "pointer",
              borderRadius: "8px 8px 0 0",
              backgroundColor:
                activeTab === tab.key ? "#056ec4" : "#f0f0f0",
              color: activeTab === tab.key ? "#fff" : "#056ec4",
              fontWeight: activeTab === tab.key ? 600 : 500,
              boxShadow:
                activeTab === tab.key
                  ? "0 2px 6px rgba(0,0,0,0.1)"
                  : "none",
              transition: "all 0.2s ease",
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Agent Table */}
      <table
        className="record-table"
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #ddd" }}>
            <th style={{ padding: "8px", textAlign: "center" }}>Agent Code</th>
            <th style={{ padding: "8px", textAlign: "center" }}>Agent Name</th>
            <th style={{ padding: "8px", textAlign: "center" }}>Email</th>
            <th style={{ padding: "8px", textAlign: "center" }}>Phone</th>
            <th style={{ padding: "8px", textAlign: "center" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredAgents.map((agent) => (
            <tr
              key={agent.id}
              onClick={() => onSelectAgent && onSelectAgent(agent)}
              style={{
                cursor: "pointer",
                borderBottom: "1px solid #eee",
                backgroundColor:
                  selectedAgent?.id === agent.id ? "#f0f8ff" : "transparent",
              }}
            >
              <td style={{ padding: "8px", textAlign: "center" }}>
                {agent.agentCode}
              </td>
              <td style={{ padding: "8px", textAlign: "center" }}>
                {agent.agentName}
              </td>
              <td style={{ padding: "8px", textAlign: "center" }}>
                {agent.email}
              </td>
              <td style={{ padding: "8px", textAlign: "center" }}>
                {agent.phone}
              </td>
              <td style={{ padding: "8px", textAlign: "center" }}>
                <StatusBadge status={agent.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AgentList;
